import { ArrowRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

export function Home() {
  const { theme } = useTheme();
  const navigate = useNavigate();

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className={`absolute inset-0 ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-dark-100 via-dark-200 to-dark-300'
              : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-white'
          } animate-gradient bg-[length:400%_400%]`}></div>
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <div className="absolute -top-20 -left-20 w-72 h-72 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float"></div>
              <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float" style={{ animationDelay: '2s' }}></div>
              <h1 className="text-6xl md:text-8xl font-normal mb-8 leading-tight">
                <span className="text-gradient">Solving</span> intelligence to advance science and humanity
              </h1>
              <div className="flex items-center space-x-6">
                <button 
                  onClick={() => navigate('/research')}
                  className="group inline-flex items-center px-6 py-3 text-lg text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-all duration-300 shadow-soft hover:shadow-lg"
                >
                  Explore our research 
                  <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-radial from-primary-500/20 to-transparent opacity-70"></div>
              <img
                src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80"
                alt="AI Research"
                className="w-full h-[600px] object-cover rounded-3xl shadow-2xl hover-card"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Research Section */}
      <section className={`py-32 ${
        theme === 'dark' 
          ? 'bg-dark-200'
          : 'bg-gradient-to-b from-white to-gray-50'
      }`}>
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-normal mb-16">
            Latest <span className="text-gradient">research</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                image: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&q=80",
                title: "Victor Academy: Revolutionizing education through AI-powered personalized learning",
                description: "A hub for human acceleration through innovative education technology",
                path: "/projects"
              },
              {
                image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80",
                title: "Daedalus: Advanced code reasoning models for software engineering",
                description: "AI models optimized for code generation, debugging, and algorithmic reasoning",
                path: "/models"
              },
              {
                image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80",
                title: "Apollo Model Family: Consistent instruction-following across scales",
                description: "Open models designed for versatile reasoning and consistent performance",
                path: "/models"
              }
            ].map((item, index) => (
              <div 
                key={index} 
                className={`group cursor-pointer rounded-2xl p-6 hover-card ${
                  theme === 'dark' 
                    ? 'bg-dark-300 hover:bg-dark-400' 
                    : 'bg-white hover:bg-gray-50'
                }`}
                onClick={() => navigate(item.path)}
              >
                <div className="relative overflow-hidden rounded-xl mb-6">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-[300px] object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-xl font-medium mb-2 group-hover:text-primary-500 transition-colors duration-200">
                  {item.title}
                </h3>
                <p className={`${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                } group-hover:text-primary-700`}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-32">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-normal mb-6">
                Real-world <span className="text-gradient">impact</span>
              </h2>
              <p className={`text-xl leading-relaxed mb-8 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                We're committed to developing artificial intelligence safely and ethically, 
                ensuring our research benefits humanity.
              </p>
              <Link 
                to="/safety"
                className="group inline-flex items-center px-6 py-3 text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
              >
                Learn about our approach 
                <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-4">
                <div className="w-full h-full mx-auto rotate-3 bg-gradient-to-r from-primary-500 to-indigo-600 opacity-30 blur-3xl"></div>
              </div>
              <img
                src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80"
                alt="Impact"
                className="relative rounded-3xl shadow-2xl hover-card"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}